import React, { useEffect, useRef, useState } from "react";
import Button from "components/ui/Button";
import Modal from "components/ui/Modal";
import AnnouncementAudience from "./AnnouncementAudience";
import AnnouncementMedia from "./AnnouncementMedia";
import AnnouncementPreview from "./AnnouncementPreview";
import {
  EMPTY_PRODUCT_ANNOUNCEMENT,
  PRODUCT_ANNOUNCEMENT_KINDS,
  PRODUCT_ANNOUNCEMENT_TARGETS,
  createAnnouncementAction,
  createStagedMedia,
  formatAnnouncementDate,
  getApiErrorMessage,
  releaseStagedMedia,
  toAnnouncementForm,
  toAnnouncementPayload,
} from "utils/productAnnouncement";

const fieldClass =
  "w-full rounded-md border border-mediumgray bg-white px-3 py-2 text-sm outline-none focus:border-darkgray";
const labelClass = "mb-1 block text-sm font-medium text-darkergray";
const disabledClass = "disabled:cursor-not-allowed disabled:opacity-40";

const Section = ({ title, description, children }) => (
  <section className="rounded-lg border border-mediumgray bg-white p-4">
    <h3 className="text-base font-semibold text-darkergray">{title}</h3>
    {description && <p className="mt-1 text-xs text-darkgray">{description}</p>}
    <div className="mt-4 space-y-4">{children}</div>
  </section>
);

const AnnouncementEditor = ({
  title,
  announcement,
  isLoading,
  loadError,
  onSave,
  onPublish,
  onNotifyAgain,
  onUploadMedia,
  onDeleteMedia,
  onClose,
  onDirtyChange,
  isSaving,
  isMediaBusy,
}) => {
  const [form, setForm] = useState(() =>
    toAnnouncementForm(announcement || EMPTY_PRODUCT_ANNOUNCEMENT),
  );
  const [reviewAction, setReviewAction] = useState(null);
  const [error, setError] = useState("");
  const [isDirty, setIsDirty] = useState(false);
  const [hasPendingFile, setHasPendingFile] = useState(false);
  const loadedAnnouncementIdRef = useRef(null);
  const formMediaRef = useRef(form.media);
  formMediaRef.current = form.media;
  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  useEffect(() => {
    const announcementId = announcement?._id || "new";
    if (loadedAnnouncementIdRef.current === announcementId) return;
    loadedAnnouncementIdRef.current = announcementId;
    releaseStagedMedia(formMediaRef.current);
    setForm(toAnnouncementForm(announcement || EMPTY_PRODUCT_ANNOUNCEMENT));
    setIsDirty(false);
  }, [announcement]);

  useEffect(() => () => releaseStagedMedia(formMediaRef.current), []);

  useEffect(() => {
    onDirtyChange?.(isDirty || hasPendingFile);
  }, [isDirty, hasPendingFile, onDirtyChange]);

  const isDraft = !announcement || announcement.publicationState === "draft";
  const isPublished = announcement?.publicationState === "published";
  const isBusy = isSaving || isMediaBusy;
  const stagedMedia = form.media.filter((asset) => asset.file);
  const canSave = isDirty && !hasPendingFile && !isBusy;
  const canReview = !hasPendingFile && !isBusy;
  const saveState = hasPendingFile
    ? "A media file is chosen but not added yet."
    : isDirty
      ? "Unsaved changes"
      : announcement
        ? "All changes saved"
        : "";

  const updateField = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setIsDirty(true);
    setError("");
  };

  const updateAction = (index, key, value) => {
    setForm((current) => ({
      ...current,
      actions: current.actions.map((action, actionIndex) =>
        actionIndex === index ? { ...action, [key]: value } : action,
      ),
    }));
    setIsDirty(true);
    setError("");
  };

  const updateAudience = (audience) => {
    setForm((current) => ({ ...current, audience }));
    setIsDirty(true);
    setError("");
  };

  const addAction = () => {
    if (form.actions.length >= 3) return;
    const action = createAnnouncementAction();
    if (form.actions.length) action.style = "secondary";
    updateField("actions", [...form.actions, action]);
  };

  const removeAction = (index) => {
    updateField(
      "actions",
      form.actions.filter((_, actionIndex) => actionIndex !== index),
    );
  };

  const moveAction = (index, direction) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= form.actions.length) return;
    const actions = [...form.actions];
    [actions[index], actions[nextIndex]] = [actions[nextIndex], actions[index]];
    updateField("actions", actions);
  };

  const updateHighlight = (index, value) => {
    setForm((current) => ({
      ...current,
      highlights: current.highlights.map((item, itemIndex) =>
        itemIndex === index ? value : item,
      ),
    }));
    setIsDirty(true);
  };

  const addHighlight = () => {
    if (form.highlights.length >= 6) return;
    updateField("highlights", [...form.highlights, ""]);
  };

  const removeHighlight = (index) => {
    updateField(
      "highlights",
      form.highlights.filter((_, itemIndex) => itemIndex !== index),
    );
  };

  const updateMedia = (index, key, value) => {
    setForm((current) => ({
      ...current,
      media: current.media.map((asset, assetIndex) =>
        assetIndex === index ? { ...asset, [key]: value } : asset,
      ),
    }));
    setIsDirty(true);
  };

  const moveMedia = (index, direction) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= form.media.length) return;
    setForm((current) => {
      const media = [...current.media];
      [media[index], media[nextIndex]] = [media[nextIndex], media[index]];
      return { ...current, media };
    });
    setIsDirty(true);
  };

  const validateForPublication = () => {
    const errors = [];
    const isInternalPath = (value) =>
      !value || (value.startsWith("/") && !value.startsWith("//"));
    if (form.name.trim().length < 3) {
      errors.push("Add an internal name of at least 3 characters");
    }
    if (form.title.trim().length < 3) {
      errors.push("Add a headline of at least 3 characters");
    }
    if (!form.body.trim()) errors.push("Add a recipient message");
    if (!form.audience.targets.length) {
      errors.push("Select at least one App or Web audience");
    }
    if (form.audience.targets.includes("webUser")) {
      errors.push("Agent Web delivery is not available yet");
    }
    if (
      form.audience.scope === "organizations" &&
      !form.audience.organizationIds.length
    ) {
      errors.push("Select at least one organization");
    }
    if (
      form.audience.scope === "accounts" &&
      !form.audience.accounts.length
    ) {
      errors.push("Select at least one person");
    }
    if (
      form.startsAt &&
      form.endsAt &&
      new Date(form.endsAt) <= new Date(form.startsAt)
    ) {
      errors.push("End time must be after the start time");
    }
    if (form.endsAt && new Date(form.endsAt) <= new Date()) {
      errors.push("End time must be in the future when publishing or notifying again");
    }
    if (
      form.media.some(
        (asset) => asset.type === "image" && !asset.altText?.trim(),
      )
    ) {
      errors.push("Add alternative text to every image");
    }
    form.actions.forEach((action, index) => {
      const actionName = `Button ${index + 1}`;
      if (!action.label.trim()) errors.push("Add an action button label");
      if (!isInternalPath(action.appPath)) {
        errors.push(`${actionName} App path must begin with one /`);
      }
      if (!isInternalPath(action.webPath)) {
        errors.push(`${actionName} Web path must begin with one /`);
      }
      if (action.externalUrl) {
        try {
          const url = new URL(action.externalUrl);
          if (
            url.protocol !== "https:" ||
            (url.hostname !== "beleef.com.au" &&
              !url.hostname.endsWith(".beleef.com.au"))
          ) {
            errors.push(
              `${actionName} external URL must be an approved Beleef HTTPS URL`,
            );
          }
        } catch {
          errors.push(`${actionName} external URL is invalid`);
        }
      }
      const hasAppTarget = form.audience.targets.some((target) =>
        target.startsWith("mobile"),
      );
      if (hasAppTarget && !action.appPath && !action.externalUrl) {
        errors.push("App audiences need an App path or external URL");
      }
      if (
        form.audience.targets.includes("webUser") &&
        !action.webPath &&
        !action.externalUrl
      ) {
        errors.push("Web Agents need a Web path or external URL");
      }
    });
    return errors;
  };

  const handleSave = async () => {
    if (!canSave) return;
    if (form.name.trim().length < 3) {
      setError("Add an internal name of at least 3 characters before saving.");
      return;
    }
    try {
      const saved = await onSave(toAnnouncementPayload(form), stagedMedia);
      if (saved) {
        releaseStagedMedia(stagedMedia);
        setForm(toAnnouncementForm(saved));
        setIsDirty(false);
      }
    } catch (saveError) {
      setError(getApiErrorMessage(saveError, "Could not save the announcement."));
    }
  };

  const beginReview = (action) => {
    const errors = validateForPublication();
    if (errors.length) {
      setError(errors.join(". "));
      return;
    }
    setError("");
    setReviewAction(action);
  };

  const confirmReview = async () => {
    try {
      const payload = toAnnouncementPayload(form);
      if (reviewAction === "publish") await onPublish(payload, stagedMedia);
      if (reviewAction === "notify") await onNotifyAgain(payload);
    } catch (publishError) {
      setReviewAction(null);
      setError(
        getApiErrorMessage(publishError, "Could not publish the announcement."),
      );
    }
  };

  const addMedia = async (file, altText, caption) => {
    const asset = announcement
      ? await onUploadMedia(file, altText, caption)
      : createStagedMedia(file, altText, caption);
    setForm((current) => ({ ...current, media: [...current.media, asset] }));
    if (!announcement) setIsDirty(true);
    setError("");
  };

  const removeMedia = async (removed) => {
    if (removed.file) {
      releaseStagedMedia([removed]);
    } else {
      await onDeleteMedia(removed._id);
    }
    setForm((current) => ({
      ...current,
      media: current.media.filter((asset) => asset !== removed),
    }));
  };

  if (isLoading || loadError) {
    return (
      <Modal isOpen onClose={onClose} title={title} className="max-w-7xl">
        {loadError ? (
          <div role="alert" className="rounded-md bg-red-50 p-4 text-sm text-red-700">
            {loadError}
          </div>
        ) : (
          <div className="py-12 text-center">Loading announcement…</div>
        )}
      </Modal>
    );
  }

  const errorMessage = error ? (
    <div role="alert" className="max-h-20 flex-1 overflow-y-auto text-sm text-red-700">
      {error}
    </div>
  ) : (
    <div
      className={`flex-1 text-sm ${
        hasPendingFile ? "text-amber-800" : "text-darkgray"
      }`}
    >
      {saveState}
    </div>
  );

  if (reviewAction) {
    const actionLabel =
      reviewAction === "publish" ? "Publish announcement" : "Notify again";
    return (
      <Modal
        isOpen
        onClose={() => !isBusy && onClose()}
        title={title}
        className="max-w-7xl"
        footer={
          <div className="flex flex-wrap items-center justify-end gap-2">
            {errorMessage}
            <Button
              onClick={() => setReviewAction(null)}
              className="gray-button"
              disabled={isBusy}
            >
              Back to edit
            </Button>
            <Button
              onClick={confirmReview}
              className="black-button"
              loading={isSaving}
              disabled={isBusy}
            >
              Confirm {actionLabel.toLowerCase()}
            </Button>
          </div>
        }
      >
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <h3 className="font-semibold text-amber-900">Final review</h3>
          <p className="mt-1 text-sm text-amber-800">
            {reviewAction === "publish"
              ? "This makes the announcement eligible for its selected audiences at the scheduled time."
              : "This creates a new delivery version. People who finished or dismissed the earlier version can receive it again."}
          </p>
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            {form.audience.targets.map((target) => (
              <span key={target} className="rounded-full bg-white px-2 py-1">
                {
                  PRODUCT_ANNOUNCEMENT_TARGETS.find(
                    (option) => option.value === target,
                  )?.label
                }
              </span>
            ))}
          </div>
          <p className="mt-3 text-xs text-amber-900">
            Scope: {form.audience.scope === "organizations"
              ? `${form.audience.organizationIds.length} selected organization(s)`
              : form.audience.scope === "accounts"
                ? `${form.audience.accounts.length} selected person/people`
                : "Everyone in selected roles"}
            <br />
            Starts:{" "}
            {form.startsAt
              ? formatAnnouncementDate(form.startsAt)
              : "On publish"}{" "}
            · Ends:{" "}
            {formatAnnouncementDate(form.endsAt)}
          </p>
        </div>
        <AnnouncementPreview announcement={form} />
      </Modal>
    );
  }

  return (
    <Modal
      isOpen
      onClose={() => !isBusy && onClose()}
      title={title}
      className="max-w-7xl"
      footer={
        <div className="flex flex-wrap items-center justify-end gap-2">
          {errorMessage}
          <Button onClick={onClose} className="gray-button" disabled={isBusy}>Cancel</Button>
          <Button onClick={handleSave} className={`gray-button ${disabledClass}`} loading={isSaving} disabled={!canSave}>
            {announcement ? "Save changes" : "Save draft"}
          </Button>
          {isDraft && (
            <Button onClick={() => beginReview("publish")} className={`black-button ${disabledClass}`} disabled={!canReview}>
              Review &amp; publish
            </Button>
          )}
          {isPublished && (
            <Button onClick={() => beginReview("notify")} className={`black-button ${disabledClass}`} disabled={!canReview}>
              Save &amp; notify again
            </Button>
          )}
        </div>
      }
    >
      {isPublished && (
        <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          Saving changes updates what pending recipients will see, but it does
          not reopen the announcement for people who already dismissed its
          current version. Use “Save &amp; notify again” only when it should be
          presented again.
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="space-y-5">
          <Section
            title="Content"
            description="Internal name is for admins; headline and message are recipient-facing."
          >
            <div>
              <label className={labelClass} htmlFor="announcement-internal-name">
                Internal name (admins only)
              </label>
              <input
                id="announcement-internal-name"
                className={fieldClass}
                maxLength={100}
                value={form.name}
                onChange={(event) => updateField("name", event.target.value)}
                placeholder="e.g. Buyer match filters · September 2026"
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="announcement-kind">
                Announcement type
              </label>
              <select
                id="announcement-kind"
                className={fieldClass}
                value={form.kind}
                onChange={(event) => updateField("kind", event.target.value)}
              >
                {PRODUCT_ANNOUNCEMENT_KINDS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className={labelClass} htmlFor="announcement-title">
                  Headline
                </label>
                <span className="text-xs text-darkgray">{form.title.length}/80</span>
              </div>
              <input
                id="announcement-title"
                className={fieldClass}
                maxLength={80}
                value={form.title}
                onChange={(event) => updateField("title", event.target.value)}
                placeholder="A clear, useful headline"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className={labelClass} htmlFor="announcement-body">
                  Message
                </label>
                <span className="text-xs text-darkgray">{form.body.length}/1500</span>
              </div>
              <textarea
                id="announcement-body"
                className={`${fieldClass} min-h-32 resize-y`}
                maxLength={1500}
                value={form.body}
                onChange={(event) => updateField("body", event.target.value)}
                placeholder="Explain what changed and why it helps."
              />
            </div>
            <div>
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-medium text-darkergray">Key points</span>
                <button
                  type="button"
                  onClick={addHighlight}
                  disabled={form.highlights.length >= 6}
                  className="text-xs font-medium text-blue-700 disabled:opacity-40"
                >
                  + Add point
                </button>
              </div>
              <div className="space-y-2">
                {form.highlights.map((highlight, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      aria-label={`Key point ${index + 1}`}
                      className={fieldClass}
                      maxLength={160}
                      value={highlight}
                      onChange={(event) => updateHighlight(index, event.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => removeHighlight(index)}
                      className="px-2 text-red-600"
                      aria-label={`Remove key point ${index + 1}`}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </Section>

          <AnnouncementAudience
            audience={form.audience}
            accountDetails={form.audienceAccountDetails}
            onChange={updateAudience}
          />

          <Section
            title="Schedule and order"
            description="Leave start blank to go live when published. Leave end blank for no automatic end."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass} htmlFor="announcement-starts-at">
                  Starts ({browserTimeZone})
                </label>
                <input
                  id="announcement-starts-at"
                  type="datetime-local"
                  className={fieldClass}
                  value={form.startsAt}
                  onChange={(event) => updateField("startsAt", event.target.value)}
                />
              </div>
              <div>
                <label className={labelClass} htmlFor="announcement-ends-at">
                  Ends ({browserTimeZone})
                </label>
                <input
                  id="announcement-ends-at"
                  type="datetime-local"
                  className={fieldClass}
                  value={form.endsAt}
                  onChange={(event) => updateField("endsAt", event.target.value)}
                />
              </div>
            </div>
            <div>
              <label className={labelClass} htmlFor="announcement-priority">
                Delivery order
              </label>
              <select
                id="announcement-priority"
                className={fieldClass}
                value={form.priority}
                onChange={(event) =>
                  updateField("priority", Number(event.target.value))
                }
              >
                <option value="25">Low</option>
                <option value="50">Normal</option>
                <option value="75">High</option>
                <option value="100">Urgent</option>
              </select>
              <p className="mt-1 text-xs text-darkgray">
                Higher-order announcements appear first when several are pending.
              </p>
            </div>
          </Section>
        </div>

        <div className="space-y-5">
          <AnnouncementMedia
            media={form.media}
            isNew={!announcement}
            isDraft={isDraft}
            isPublished={isPublished}
            isMediaBusy={isMediaBusy}
            onUpdate={updateMedia}
            onMove={moveMedia}
            onAdd={addMedia}
            onRemove={removeMedia}
            onError={setError}
            onPendingFileChange={setHasPendingFile}
          />

          <Section
            title="Action buttons"
            description="Add up to 3 ordered buttons. Use internal paths beginning with /. External links are limited to Beleef HTTPS domains."
          >
            <div className="space-y-3">
              {form.actions.map((action, index) => (
                <div
                  key={action._id || action.clientId}
                  className="rounded-lg border border-mediumgray p-3"
                >
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-sm font-medium text-darkergray">
                      Button {index + 1}
                    </span>
                    <div className="flex gap-3 text-xs">
                      <button type="button" disabled={index === 0} onClick={() => moveAction(index, -1)} className="text-blue-700 disabled:opacity-30">Move up</button>
                      <button type="button" disabled={index === form.actions.length - 1} onClick={() => moveAction(index, 1)} className="text-blue-700 disabled:opacity-30">Move down</button>
                      <button type="button" onClick={() => removeAction(index)} className="text-red-700">Remove</button>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <label className={labelClass} htmlFor={`announcement-action-label-${index}`}>Button label</label>
                        <input id={`announcement-action-label-${index}`} className={fieldClass} maxLength={40} value={action.label} onChange={(event) => updateAction(index, "label", event.target.value)} placeholder="e.g. Try buyer matching" />
                      </div>
                      <div>
                        <label className={labelClass} htmlFor={`announcement-action-style-${index}`}>Style</label>
                        <select id={`announcement-action-style-${index}`} className={fieldClass} value={action.style} onChange={(event) => updateAction(index, "style", event.target.value)}>
                          <option value="primary">Primary</option>
                          <option value="secondary">Secondary</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <label className={labelClass} htmlFor={`announcement-app-path-${index}`}>App path</label>
                        <input id={`announcement-app-path-${index}`} className={fieldClass} value={action.appPath} onChange={(event) => updateAction(index, "appPath", event.target.value)} placeholder="/(tabs)/home" />
                      </div>
                      <div>
                        <label className={labelClass} htmlFor={`announcement-web-path-${index}`}>Web path</label>
                        <input id={`announcement-web-path-${index}`} className={fieldClass} value={action.webPath} onChange={(event) => updateAction(index, "webPath", event.target.value)} placeholder="/buyer-matches" />
                      </div>
                    </div>
                    <div>
                      <label className={labelClass} htmlFor={`announcement-external-url-${index}`}>External Beleef URL</label>
                      <input id={`announcement-external-url-${index}`} type="url" className={fieldClass} value={action.externalUrl} onChange={(event) => updateAction(index, "externalUrl", event.target.value)} placeholder="https://beleef.com.au/..." />
                    </div>
                  </div>
                </div>
              ))}
              {form.actions.length < 3 && (
                <button type="button" onClick={addAction} className="rounded-md border border-mediumgray px-3 py-2 text-sm font-medium text-darkergray">
                  + Add action button
                </button>
              )}
            </div>
          </Section>

          <AnnouncementPreview announcement={form} />
        </div>
      </div>
    </Modal>
  );
};

export default AnnouncementEditor;
